"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useVirtualPageview = exports.useTrack = void 0;
var react_1 = require("react");
var index_1 = __importDefault(require("./index"));
var components_1 = require("./components");
/**
 * @description A React hook for retrieving a reference to the
 * {@linkcode SegmentSDKWrapper.track} method that is bound to the current
 * tracking context in the component tree. When using this hook, your track
 * calls will receive properties set by {@linkcode TrackingProvider},
 * {@linkcode trackingProvider}, {@linkcode SegmentSDKWrapper.init},
 * {@linkcode SegmentSDKWrapper.setStaticProperty},
 * {@linkcode SegmentSDKWrapper.unsetStaticProperty} or
 * {@linkcode TrackingContext}.
 *
 * Properties are attached to Segment events dispatched by this hook from four
 * different places: the tracking context, the hook invocation, the SDK wrapper
 * state, and the invocation of the track function returned by the hook. These
 * properties are merged with the following order of precedence:
 *
 * ```javascript
 * {
 *   ...propertiesFromSDKWrapperState,
 *   ...propertiesFromTrackingContext,
 *   ...propertiesFromHookInvocation,
 *   ...propertiesFromTrackFunctionInvocation,
 * }
 * ```
 *
 * Options are attached to Segment events dispatched by this hook from three
 * different places: the SDK wrapper state, the hook invocation and the tracking
 * function invocation. Options are merged with the following order of
 * precedence:
 *
 * ```javascript
 * {
 *   ...optionsFromSDKWrapperState,
 *   ...optionsFromHookInvocation,
 *   ...optionsFromTrackFunctionInvocation,
 * }
 * ```
 *
 * Example:
 *
 * ```jsx
 * import { useTrack } from '@optimizely/segment-sdk-wrapper';
 *
 * // Function-based React component:
 * const RolloutManager = (props) => {
 *   const track = useTrack({ editingRollout: props.rolloutId });
 *
 *   return (
 *     <button onClick={() => track('click', { clickedOn: 'button' })} />
 *   );
 * }
 * ```
 *
 * @category Hook
 */
exports.useTrack = function (properties, options) {
    var ancestorProperties = react_1.useContext(components_1.TrackingContext);
    /**
     * Memoize the returned bound track function so that it is only regenerated if
     * the properties or options change.
     */
    return react_1.useMemo(function () { return function (event, trackInvocationProperties, trackInvocationOptions, callback) {
        return index_1.default.track(event, __assign(__assign(__assign({}, ancestorProperties), properties), trackInvocationProperties), __assign(__assign({}, options), trackInvocationOptions), callback);
    }; }, [properties, options, ancestorProperties]);
};
/**
 * @description A React hook for tracking when a page component mounts using the
 * {@linkcode SegmentSDKWrapper.virtualPageview} method and the current
 * tracking context in the component tree. When using this hook, your track
 * calls will receive properties set by {@linkcode TrackingProvider},
 * {@linkcode trackingProvider}, {@linkcode SegmentSDKWrapper.init},
 * {@linkcode SegmentSDKWrapper.setStaticProperty},
 * {@linkcode SegmentSDKWrapper.unsetStaticProperty} or
 * {@linkcode TrackingContext}.
 *
 * Properties are attached to Segment events dispatched by this hook from three
 * different places: the tracking context, the hook invocation, and the SDK
 * wrapper state. These properties are merged with the following order of
 * precedence:
 *
 * ```javascript
 * {
 *   ...propertiesFromSDKWrapperState,
 *   ...propertiesFromTrackingContext,
 *   ...propertiesFromHookInvocation,
 * }
 * ```
 *
 * Options are attached to Segment events dispatched by this hook from two
 * different places: the SDK wrapper state and the hook invocation. Options are
 * merged with the following order of precedence:
 *
 * ```javascript
 * {
 *   ...optionsFromSDKWrapperState,
 *   ...optionsFromHookInvocation,
 * }
 * ```
 *
 * Example:
 *
 * ```javascript
 * import { useVirtualPageview } from '@optimizely/segment-sdk-wrapper';
 *
 * // Function-based React component:
 * const RolloutManager = (props) => {
 *   useVirtualPageview(
 *     'dashboard',
 *     'rollouts',
 *     'flags',
 *     { editingRollout: props.rolloutId },
 *   );
 *
 *   return (...);
 * }
 * ```
 *
 * @category Hook
 */
exports.useVirtualPageview = function (category, name, urlSuffix, properties, options, callback) {
    var ancestorProperties = react_1.useContext(components_1.TrackingContext);
    // Effect only executes after the component mounts
    react_1.useEffect(function () {
        index_1.default.virtualPageview(category, name, urlSuffix, __assign(__assign({}, ancestorProperties), properties), options, callback);
    }, []);
};
exports.default = {
    useTrack: exports.useTrack,
    useVirtualPageview: exports.useVirtualPageview,
};
